class EnhancedProductImageGallery {
  constructor() {
    this.modal = null;
    this.sectionId = null;
    this.currentImageIndex = 0;
    this.init();
  }

  init() {
    const observer = new MutationObserver(() => {
      requestAnimationFrame(() => {
        this.setupGallery();
        observer.disconnect();
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  setupGallery() {
    const mediaGallery = document.querySelector('media-gallery[data-desktop-layout="grid"]');
    if (!mediaGallery) return;

    this.sectionId = mediaGallery.id.replace('MediaGallery-', '');
    this.modal = document.getElementById(`ProductImageGalleryModal-${this.sectionId}`);
    if (!this.modal) return;

    const triggers = document.querySelectorAll('.enhanced-gallery-trigger');
    if (!triggers.length) return;

    this.bindTriggers(triggers);
    this.bindModalControls();
    this.bindKeyboardNavigation();
  }

  bindTriggers(triggers) {
    triggers.forEach((trigger, index) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.openGallery(index);
      });
    });

    document.querySelectorAll('.product__media-grid-item').forEach((item, index) => {
      item.addEventListener('click', (e) => {
        if (!e.target.closest('.enhanced-gallery-trigger')) {
          e.preventDefault();
          this.openGallery(index);
        }
      });
    });
  }

  bindModalControls() {
    const overlay = this.modal.querySelector('.product-image-gallery-modal__overlay');
    const closeBtn = this.modal.querySelector('.product-image-gallery-modal__close');
    const thumbnails = this.modal.querySelectorAll('.product-image-gallery-modal__thumbnail');

    if (closeBtn) closeBtn.addEventListener('click', () => this.closeGallery());
    if (overlay) overlay.addEventListener('click', (e) => {
      if (e.target === overlay) this.closeGallery();
    });

    thumbnails.forEach((thumb, index) => {
      thumb.addEventListener('click', () => this.setActiveImage(index));
    });

    this.bindThumbnailNavigation();
  }

  bindKeyboardNavigation() {
    document.addEventListener('keydown', (e) => {
      if (!this.modal.classList.contains('active')) return;
      if (e.key === 'Escape') this.closeGallery();
      if (e.key === 'ArrowLeft') this.previousImage();
      if (e.key === 'ArrowRight') this.nextImage();
    });
  }

  openGallery(index = 0) {
    this.currentImageIndex = index;
    this.modal.classList.add('active');
    document.body.style.overflow = 'hidden';
    this.setActiveImage(index);
    this.modal.querySelector('.product-image-gallery-modal__close')?.focus();
  }

  closeGallery() {
    this.modal.classList.remove('active');
    document.body.style.overflow = '';
    document.querySelectorAll('.enhanced-gallery-trigger')[this.currentImageIndex]?.focus();
  }

  setActiveImage(index) {
    const thumbnails = this.modal.querySelectorAll('.product-image-gallery-modal__thumbnail');
    const mainImage = this.modal.querySelector(`#gallery-main-image-${this.sectionId}`);
    if (!thumbnails[index] || !mainImage) return;

    thumbnails.forEach((thumb, i) => thumb.classList.toggle('active', i === index));

    const src = thumbnails[index].dataset.imageSrc;
    const alt = thumbnails[index].dataset.imageAlt || '';

    mainImage.style.opacity = '0.3';
    const preload = new Image();
    preload.onload = () => {
      mainImage.src = src;
      mainImage.alt = alt;
      requestAnimationFrame(() => {
        mainImage.style.transition = 'opacity 0.4s ease-in-out';
        mainImage.style.opacity = '1';
      });
    };
    preload.onerror = () => {
      console.warn('Image failed to load:', src);
      mainImage.style.opacity = '1';
    };
    preload.src = src;

    this.currentImageIndex = index;
    this.scrollThumbnailIntoView(thumbnails[index]);
  }

  scrollThumbnailIntoView(thumbnail) {
    const container = this.modal.querySelector('.product-image-gallery-modal__thumbnails');
    if (!container || !thumbnail) return;

    const isMobile = window.innerWidth <= 768;
    const scrollTo = isMobile
      ? { left: thumbnail.offsetLeft - container.clientWidth / 2 + thumbnail.offsetWidth / 2 }
      : { top: thumbnail.offsetTop - container.clientHeight / 2 + thumbnail.offsetHeight / 2 };

    container.scrollTo({ ...scrollTo, behavior: 'smooth' });
  }

  nextImage() {
    const total = this.modal.querySelectorAll('.product-image-gallery-modal__thumbnail').length;
    this.setActiveImage((this.currentImageIndex + 1) % total);
  }

  previousImage() {
    const total = this.modal.querySelectorAll('.product-image-gallery-modal__thumbnail').length;
    this.setActiveImage((this.currentImageIndex - 1 + total) % total);
  }

  bindThumbnailNavigation() {
    const container = this.modal.querySelector('.product-image-gallery-modal__thumbnails');
    if (!container) return;

    const scroll = (dir, axis = 'vertical') => {
      const delta = 80 * (dir === 'next' ? 1 : -1);
      const options = axis === 'horizontal' ? { left: delta } : { top: delta };
      container.scrollBy({ ...options, behavior: 'smooth' });
    };

    this.modal.querySelector('.product-image-gallery-modal__thumbnail-nav--up')?.addEventListener('click', () => scroll('prev'));
    this.modal.querySelector('.product-image-gallery-modal__thumbnail-nav--down')?.addEventListener('click', () => scroll('next'));
    this.modal.querySelector('.product-image-gallery-modal__thumbnail-nav--left')?.addEventListener('click', () => scroll('prev', 'horizontal'));
    this.modal.querySelector('.product-image-gallery-modal__thumbnail-nav--right')?.addEventListener('click', () => scroll('next', 'horizontal'));

    const debounce = (fn, delay = 150) => {
      let timer;
      return () => {
        clearTimeout(timer);
        timer = setTimeout(fn, delay);
      };
    };

    const updateScrollState = debounce(() => {
      const isDesktop = window.innerWidth > 768;
      const scrollable = isDesktop
        ? container.scrollHeight > container.clientHeight
        : container.scrollWidth > container.clientWidth;

      container.classList.toggle('scrollable', scrollable);
    });

    container.addEventListener('scroll', updateScrollState);
    window.addEventListener('resize', updateScrollState);
    setTimeout(updateScrollState, 100);
  }
}

// Initialize once DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.EnhancedProductImageGallery = new EnhancedProductImageGallery();
});